/* eslint-disable max-len */
/* eslint-disable vue/max-len */
import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import * as pbcm from '@/grpc-gen/contact_manager_service_pb';
import * as pbd from '@/grpc-gen/dashboard_service_pb';
import * as pbr from '@/grpc-gen/reports_service_pb';
import * as pbs from '@/grpc-gen/settings_service_pb';
import * as pbh from '@/grpc-gen/history_service_pb';
import * as pbm from '@/grpc-gen/message_service_pb';
import * as pba from '@/grpc-gen/apps_service_pb';
import * as pbsm from '@/grpc-gen/service_manager_service_pb';
import * as pbgc from '@/grpc-gen/giftcards_service_pb';
import * as pbfb from '@/grpc-gen/feedback_service_pb';
import * as pbmcm from '@/grpc-gen/marketing_campaign_manager_service_pb';
import * as pbht from '@/grpc-gen/html_template_service_pb';
import * as pbmc from '@/grpc-gen/message_center_service_pb';
import { EmptyRequestSchema } from '@/grpc-gen/empty_pb';
import {
  ReorderEntitySchema,
  ReorderRequestSchema,
} from '@/grpc-gen/reorder_pb';
import MessageCenter, { MessageCenterMapping } from '@/grpc-api/model/message-center';
import MessageTranslation from '@/grpc-api/model/message-translation';
import MessageSettings from '@/grpc-api/model/message-settings';
import MessageRule from '@/grpc-api/model/message-rule';

import {
  grpcStreamOp, grpcStreamOpAll, grpcUnaryOp, grpcUnaryOpEx, OnErrorFnc, OnMultipleDataFnc,
} from '@/services/grpc-client-utils';
import Contact from '@/grpc-api/model/contact';
import Employee from '@/grpc-api/model/employee';
import { ContactMapping } from '@/model/Contact';
import ContactFilter from '@/grpc-api/model/contact-filter';
import HtmlTemplate from '@/grpc-api/model/html-template';
import DashboardAvailability from '@/grpc-api/model/dashboard-availability';
import Campaign from '@/grpc-api/model/campaign';
import Message from '@/grpc-api/model/message';
import { ReservationMapping } from '@/model/Reservation';
import { unixFromDate } from '@/services/time-utils';
import Reservation from '@/grpc-api/model/reservation';
import App from '@/grpc-api/model/app';
import ReservationService, { ReservationServiceMapping } from '@/grpc-api/model/reservation-service';
import Currency from '@/grpc-api/model/currency';
import GiftcardProduct from '@/grpc-api/model/giftcard-product';
import GiftcardTransaction, { GiftcardTransactionListParams } from '@/grpc-api/model/giftcard-transaction';
import Giftcard, { GiftcardMapping } from '@/grpc-api/model/giftcard';
import Feedback, { FeedbackMapping } from '@/grpc-api/model/feedback';
import FeedbackSettings, { FeedbackSettingsMapping } from '@/grpc-api/model/feedback-settings';
import MarketingCampaign, { MarketingCampaignListParams, MarketingCampaignMapping } from '@/grpc-api/model/marketing-campaign';
import MarketingCampaignSettings from '@/grpc-api/model/marketing-campaign-settings';
import { MessageRuleMapping } from '@/model/MessageRule';
import MessageCenterPreferences from './model/message-center-preferences';
import WidgetAccountSetings from './model/widget-account-settings';

const baseURL = import.meta.env.VITE_ENDPOINT_GRPC!;

export interface IGrpcClientState {
  token?: string;
}

class GrpcClient {
  state: IGrpcClientState = {};

  private transport = createGrpcWebTransport({ baseUrl: baseURL });

  contactService = createClient(pbcm.ContactService, this.transport);

  contactFilterService = createClient(pbcm.ContactFilterService, this.transport);

  contactManagerService = createClient(pbcm.ContactManagerService, this.transport);

  dashboardService = createClient(pbd.DashboardService, this.transport);

  reportsService = createClient(pbr.ReportsService, this.transport);

  settingsService = createClient(pbs.SettingsService, this.transport);

  historyService = createClient(pbh.HistoryService, this.transport);

  messageService = createClient(pbm.MessageService, this.transport);

  appsService = createClient(pba.AppsService, this.transport);

  serviceManagerService = createClient(pbsm.ServiceManagerService, this.transport);

  giftcardsService = createClient(pbgc.GiftcardsService, this.transport);

  feedbackService = createClient(pbfb.FeedbackService, this.transport);

  marketingCampaignManagerService = createClient(pbmcm.MarketingCampaignManagerService, this.transport);

  htmlTemplateService = createClient(pbht.HtmlTemplateService, this.transport);

  messageCenterService = createClient(pbmc.MessageCenterService, this.transport);

  get metadata() {
    return ({
      authorization: `Bearer ${this.state.token}`,
      // deadline: `${Date.now() + 10000}`,
    });
  }

  // Contact
  async listContacts(onData: OnMultipleDataFnc<Contact>, onError: OnErrorFnc, m: ContactMapping) {
    const req = create(pbcm.ContactListRequestSchema, {});
    return grpcStreamOp(Contact, this.contactService, 'list', this.metadata, req, m, onData, onError);
  }

  async createContact(c: Contact, e: Employee|undefined, m: ContactMapping) {
    const req = create(pbcm.ContactRequestSchema, { contact: c.toGrpcModel(), employee: e ? e.toGrpcModel() : undefined });
    return grpcUnaryOp(Contact, this.contactService, 'create', this.metadata, req, m);
  }

  async updateContact(c: Contact, e: Employee|undefined, m: ContactMapping) {
    const req = create(pbcm.ContactRequestSchema, { contact: c.toGrpcModel(), employee: e ? e.toGrpcModel() : undefined });
    return grpcUnaryOp(Contact, this.contactService, 'update', this.metadata, req, m);
  }

  // ContactFilter
  async createContactFilter(cf: ContactFilter, m = undefined) {
    const req = create(pbcm.ContactFilterRequestSchema, { contactFilter: cf.toGrpcModel() });
    return grpcUnaryOp(ContactFilter, this.contactFilterService, 'create', this.metadata, req, m);
  }

  async getContactFilter(cf: ContactFilter, m = undefined) {
    const req = create(pbcm.ContactFilterIdRequestSchema, { id: BigInt(cf.id) });
    return grpcUnaryOp(ContactFilter, this.contactFilterService, 'get', this.metadata, req, m);
  }

  async updateContactFilter(cf: ContactFilter, m = undefined) {
    const req = create(pbcm.ContactFilterRequestSchema, { contactFilter: cf.toGrpcModel() });
    return grpcUnaryOp(ContactFilter, this.contactFilterService, 'update', this.metadata, req, m);
  }

  async deleteContactFilter(cf: ContactFilter, m = undefined) {
    const req = create(pbcm.ContactFilterIdRequestSchema, { id: BigInt(cf.id) });
    return grpcUnaryOp(ContactFilter, this.contactFilterService, 'delete', this.metadata, req, m);
  }

  async listContactFilters(onData: OnMultipleDataFnc<ContactFilter>, onError: OnErrorFnc, m = undefined) {
    const req = create(pbcm.ContactFilterListRequestSchema, {});
    return grpcStreamOp(ContactFilter, this.contactFilterService, 'list', this.metadata, req, m, onData, onError);
  }

  // ContactManager
  async selectedContacts(ids: number[], onData: OnMultipleDataFnc<Contact>, onError: OnErrorFnc, m?: ContactMapping) {
    const req = create(pbcm.SelectedContactsRequestSchema, { ids: ids.map((id) => BigInt(id)) });
    return grpcStreamOp(Contact, this.contactManagerService, 'selectedContacts', this.metadata, req, m, onData, onError, { waitForEnd: true });
  }

  async mergeContacts(c: Contact, e: Employee|undefined, ids: number[], onData: OnMultipleDataFnc<Contact>, onError: OnErrorFnc, m?: ContactMapping) {
    const req = create(pbcm.MergeContactsRequestSchema, {
      contact: c.toGrpcModel(),
      ids: ids.map((id) => BigInt(id)),
      employee: e ? e.toGrpcModel() : undefined,
    });
    return grpcStreamOp(Contact, this.contactManagerService, 'mergeContacts', this.metadata, req, m, onData, onError, { waitForEnd: true });
  }

  async deleteContacts(ids: number[], e: Employee|undefined, onData: OnMultipleDataFnc<Contact>, onError: OnErrorFnc, m?: ContactMapping) {
    const req = create(pbcm.DeleteContactsRequestSchema, {
      ids: ids.map((id) => BigInt(id)),
      employee: e ? e.toGrpcModel() : undefined,
    });
    return grpcStreamOp(Contact, this.contactManagerService, 'deleteContacts', this.metadata, req, m, onData, onError, { waitForEnd: true });
  }

  async searchContacts(filter: string, onData: OnMultipleDataFnc<Contact>, onError: OnErrorFnc, m?: ContactMapping) {
    const req = create(pbcm.SearchContactsRequestSchema, { filter });
    return grpcStreamOp(Contact, this.contactManagerService, 'searchContacts', this.metadata, req, m, onData, onError, { waitForEnd: true });
  }

  // Dashboard
  async getAvailability(dateIndex: number, m = undefined): Promise<DashboardAvailability> {
    const req = create(pbd.DashboardAvailabilityRequestSchema, { date: dateIndex });
    return grpcUnaryOp(DashboardAvailability, this.dashboardService, 'getAvailability', this.metadata, req, m);
  }

  // Reports
  async getTabsReports(beginDateIndex: number, endDateIndex: number, binType: pbr.ReportsParams_ReportsBinType, m = undefined): Promise<pbr.TabsReportsResponse> {
    const params = create(pbr.ReportsParamsSchema, { beginDate: beginDateIndex, endDate: endDateIndex, binType });
    const req = create(pbr.ReportsRequestSchema, { params });
    return grpcUnaryOpEx(null, this.reportsService, 'getTabsReports', this.metadata, req, m) as Promise<pbr.TabsReportsResponse>;
  }

  async getMsgsReports(beginDateIndex: number, endDateIndex: number, binType: pbr.ReportsParams_ReportsBinType, m = undefined): Promise<pbr.MsgsReportsResponse> {
    const params = create(pbr.ReportsParamsSchema, { beginDate: beginDateIndex, endDate: endDateIndex, binType });
    const req = create(pbr.ReportsRequestSchema, { params });
    return grpcUnaryOpEx(null, this.reportsService, 'getMsgsReports', this.metadata, req, m) as Promise<pbr.MsgsReportsResponse>;
  }

  // Settings
  async getDefaultCampaign(m = undefined): Promise<Campaign> {
    const req = create(EmptyRequestSchema, {});
    return grpcUnaryOp(Campaign, this.settingsService, 'getDefaultCampaign', this.metadata, req, m);
  }

  async testDefaultCampaign(data: Campaign, m = undefined): Promise<void> {
    const req = create(pbs.CampaignRequestSchema, { campaign: data.toGrpcModel() });
    const res = grpcUnaryOpEx(null, this.settingsService, 'testDefaultCampaign', this.metadata, req, m) as Promise<void>;
    return res;
  }

  async updateDefaultCampaign(data: Campaign, m = undefined): Promise<Campaign> {
    const req = create(pbs.CampaignRequestSchema, { campaign: data.toGrpcModel() });
    return grpcUnaryOp(Campaign, this.settingsService, 'updateDefaultCampaign', this.metadata, req, m);
  }

  async getAccountSettings(m = undefined): Promise<Record<string, string>> {
    const req = create(EmptyRequestSchema, {});
    const transform = (resp: pbs.AccountSettingsResponse): Record<string, string> => resp.settings ?? {};
    return grpcUnaryOpEx(transform, this.settingsService, 'getAccountSettings', this.metadata, req, m) as Promise<Record<string, string>>;
  }

  async setAccountSettings(settings: Record<string, string>, m = undefined): Promise<Record<string, string>> {
    const req = create(pbs.SetAccountSettingsRequestSchema, { settings });
    const transform = (resp: pbs.AccountSettingsResponse): Record<string, string> => resp.settings ?? {};
    return grpcUnaryOpEx(transform, this.settingsService, 'setAccountSettings', this.metadata, req, m) as Promise<Record<string, string>>;
  }

  async getWidgetAccountSettings(m = undefined): Promise<WidgetAccountSetings> {
    const req = create(EmptyRequestSchema, {});
    return grpcUnaryOp(WidgetAccountSetings, this.settingsService, 'getAccountSettings', this.metadata, req, m);
  }

  async setWidgetAccountSettings(settings: WidgetAccountSetings, m = undefined): Promise<WidgetAccountSetings> {
    const req = create(pbs.SetAccountSettingsRequestSchema, { settings: settings.toGrpcModel() });
    return grpcUnaryOp(WidgetAccountSetings, this.settingsService, 'setAccountSettings', this.metadata, req, m);
  }

  // History
  async getReservationHistory(reservationId: number, m = undefined): Promise<{ records: string[], messages: string[] }> {
    const req = create(pbh.HistoryRequestSchema, { reservationId });
    const response = await (grpcUnaryOpEx(null, this.historyService, 'getHistory', this.metadata, req, m) as Promise<pbh.HistoryResponse>);
    const records = (response.records ?? []).map((hr) => hr.json);
    const messages = (response.messages ?? []).map((hr) => hr.json);
    return { records, messages };
  }

  async resendReservationConfirmation(reservationId: number, m = undefined): Promise<void> {
    const req = create(pbh.ResendConfirmationRequestSchema, { reservationId });
    await grpcUnaryOpEx(null, this.historyService, 'resendConfirmation', this.metadata, req, m);
  }

  // Message
  async sendMessage(message: Message): Promise<void> {
    const req = create(pbm.SendMessageRequestSchema, { message: message.toGrpcModel() });
    const res = grpcUnaryOpEx(null, this.messageService, 'sendMessage', this.metadata, req, undefined) as Promise<void>;
    return res;
  }

  // Apps
  async getApp(appId: number): Promise<App> {
    const app = new App({ appId });
    const req = create(pba.AppRequestSchema, { app: app.toGrpcModel() });
    const res = grpcUnaryOp(App, this.appsService, 'getApp', this.metadata, req, undefined) as Promise<App>;
    return res;
  }

  // async installApp(appId: number, isInstalled: boolean): Promise<App> {
  //   const app = new App({ appId, isInstalled });
  //   const req = new pba.AppRequest(); req.setApp(app.toGrpcModel());
  //   const res = grpcUnaryOp(App, this.appsService, 'installApp', this.metadata, req, undefined) as Promise<App>;
  //   return res;
  // }

  async updateApp(appId: number, isInstalled: boolean, params?: string): Promise<App> {
    const app = new App({ appId, isInstalled });
    app.params = params || undefined;
    const req = create(pba.AppRequestSchema, { app: app.toGrpcModel() });
    const res = grpcUnaryOp(App, this.appsService, 'updateApp', this.metadata, req, undefined) as Promise<App>;
    return res;
  }

  // Service Manager
  async listReservationServices(onData: OnMultipleDataFnc<ReservationService>, m: ReservationServiceMapping): Promise<void> {
    const req = create(EmptyRequestSchema, {});
    return grpcStreamOp(ReservationService, this.serviceManagerService, 'listServices', this.metadata, req, m, onData, () => {}, { waitForEnd: true });
  }

  async reorderReservationServices(services: ReservationService[], onData: OnMultipleDataFnc<ReservationService>, m: ReservationServiceMapping): Promise<void> {
    const req = create(ReorderRequestSchema, {
      entities: services.map((s) => create(ReorderEntitySchema, { id: BigInt(s.id), order: s.order ?? 0 })),
    });
    return grpcStreamOp(ReservationService, this.serviceManagerService, 'reorderServices', this.metadata, req, m, onData, () => {}, { waitForEnd: true });
  }

  async updateReservationService(service: ReservationService, onData: OnMultipleDataFnc<ReservationService>, m: ReservationServiceMapping): Promise<void> {
    const req = create(pbsm.ReservationServiceRequestSchema, { service: service.toGrpcModel() });
    return grpcStreamOp(ReservationService, this.serviceManagerService, 'updateService', this.metadata, req, m, onData, () => {}, { waitForEnd: true });
  }

  async checkMergeReservationServices(service: ReservationService, toMergeServices: ReservationService[], m: ReservationServiceMapping): Promise<{ service: ReservationService, conflicts: string[] }> {
    const req = create(pbsm.MergeReservationServicesRequestSchema, {
      serviceId: BigInt(service.id),
      toMergeServiceIds: toMergeServices.map((s) => BigInt(s.id)),
    });
    const response = await (grpcUnaryOpEx(null, this.serviceManagerService, 'checkMergeServices', this.metadata, req, m) as Promise<pbsm.CheckMergeReservationServicesResponse>);
    const mergedService = response.service ? ReservationService.fromGrpcModel(response.service, m) : new ReservationService();
    const conflicts = response.conflicts ?? [];
    return { service: mergedService, conflicts };
  }

  async mergeReservationServices(service: ReservationService, toMergeServices: ReservationService[], onData: OnMultipleDataFnc<ReservationService>, m: ReservationServiceMapping): Promise<void> {
    const req = create(pbsm.MergeReservationServicesRequestSchema, {
      serviceId: BigInt(service.id),
      toMergeServiceIds: toMergeServices.map((s) => BigInt(s.id)),
    });
    return grpcStreamOp(ReservationService, this.serviceManagerService, 'mergeServices', this.metadata, req, m, onData, () => {}, { waitForEnd: true });
  }

  async listCurrencies(): Promise<Currency[]> {
    const req = create(EmptyRequestSchema, {});
    return grpcStreamOpAll(Currency, this.settingsService, 'listCurrencies', this.metadata, req);
  }

  // giftcards
  async getGiftcardProduct(): Promise<GiftcardProduct> {
    const req = create(EmptyRequestSchema, {});
    return grpcUnaryOp(GiftcardProduct, this.giftcardsService, 'getProduct', this.metadata, req, {});
  }

  async updateGiftcardProduct(p: GiftcardProduct): Promise<GiftcardProduct> {
    const req = create(pbgc.GiftcardsProductRequetsSchema, { product: p.toGrpcModel() });
    return grpcUnaryOp(GiftcardProduct, this.giftcardsService, 'updateProduct', this.metadata, req, {});
  }

  async listGiftcardTransactions(p: GiftcardTransactionListParams, onData: OnMultipleDataFnc<GiftcardTransaction>) {
    const params = create(pbgc.GiftcardsListTransactionsParamsSchema, {
      beginDate: p.beginDateIndex ?? 0,
      endDate: p.endDateIndex ?? 0,
      types: p.types ?? [],
      giftcardCodes: p.codes ?? [],
      giftcardCode: p.code ?? '',
    } as any);

    const req = create(pbgc.GiftcardsListTransactionsRequestSchema, { params });

    return grpcStreamOp(GiftcardTransaction, this.giftcardsService, 'listTransactions', this.metadata, req, {}, onData, () => {}, { waitForEnd: true });
  }

  async getGiftcard(p: { id?: number, code?: string }, m: GiftcardMapping): Promise<Giftcard> {
    const req = create(pbgc.GiftcardsGetGiftcardRequestSchema, {
      giftcardId: p.id ?? 0,
      giftcardCode: p.code ?? '',
    } as any);
    return grpcUnaryOp(Giftcard, this.giftcardsService, 'getGiftcard', this.metadata, req, m);
  }

  async redeemGiftcard(id: number, code: string, amount: number, balance: number, p: { idempotencyKey?: string}, m: GiftcardMapping): Promise<Giftcard|undefined> {
    const req = create(pbgc.GiftcardsRedeemGiftcardRequestSchema, {
      giftcardId: id,
      giftcardCode: code,
      amount,
      balance,
      idempotencyKey: p.idempotencyKey ?? '',
    } as any);

    return grpcUnaryOp(Giftcard, this.giftcardsService, 'redeemGiftcard', this.metadata, req, m);
  }

  // Feedback
  async listFeedbacks(beginDateIndex: number, endDateIndex: number, since: Date|undefined, onData: OnMultipleDataFnc<Feedback>, onError: OnErrorFnc, m: FeedbackMapping) {
    const params = create(pbfb.FeedbackListFeedbacksParamsSchema, {
      beginDate: beginDateIndex,
      endDate: endDateIndex,
      sinceDtCreate: since ? BigInt(unixFromDate(since)) : 0n,
    } as any);
    const req = create(pbfb.FeedbackListFeedbacksRequestSchema, { params });
    return grpcStreamOp(Feedback, this.feedbackService, 'listFeedbacks', this.metadata, req, m, onData, onError);
  }

  async getFeedback(id: number, m: FeedbackMapping) {
    const req = create(pbfb.FeedbackGetFeedbackRequestSchema, { feedbackId: BigInt(id) });
    return grpcUnaryOp(Feedback, this.feedbackService, 'getFeedback', this.metadata, req, m);
  }

  async getFeedbackReservation(id: number, m: ReservationMapping) {
    const req = create(pbfb.FeedbackGetReservationRequestSchema, { reservationId: BigInt(id) });
    return grpcUnaryOp(Reservation, this.feedbackService, 'getReservation', this.metadata, req, m);
  }

  async getFeedbackSettings(m: FeedbackSettingsMapping) {
    const req = create(EmptyRequestSchema, {});
    return grpcUnaryOp(FeedbackSettings, this.feedbackService, 'getSettings', this.metadata, req, m);
  }

  async setFeedbackSettings(settings: FeedbackSettings, m: FeedbackSettingsMapping) {
    const req = create(pbfb.FeedbackSetSettingsRequestSchema, { settings: settings.toGrpcModel() });
    return grpcUnaryOp(FeedbackSettings, this.feedbackService, 'setSettings', this.metadata, req, m);
  }

  // campaign manager
  async updateMarketingCampaign(campaign: MarketingCampaign, m: MarketingCampaignMapping) {
    const req = create(pbmcm.MarketingCampaignRequestSchema, { marketingCampaign: campaign.toGrpcModel() });
    return grpcUnaryOp(MarketingCampaign, this.marketingCampaignManagerService, 'updateCampaign', this.metadata, req, m);
  }

  async listMarketingCampaigns(p: MarketingCampaignListParams, onData: OnMultipleDataFnc<MarketingCampaign>, m: MarketingCampaignMapping) {
    const params = create(pbmcm.ListCampaignsRequestParamsSchema, {
      beginDate: p.beginDateIndex,
      endDate: p.endDateIndex,
      showArchived: p.showArchived,
    } as any);
    const req = create(pbmcm.ListCampaignsRequestSchema, { params });
    return grpcStreamOp(MarketingCampaign, this.marketingCampaignManagerService, 'listCampaigns', this.metadata, req, m, onData, () => {}, { waitForEnd: false, maxDataCount: 1 });
  }

  async listAllContactFilters(onData: OnMultipleDataFnc<ContactFilter>, m = undefined) {
    const req = create(pbcm.ContactFilterListRequestSchema, {});
    return grpcStreamOp(ContactFilter, this.contactFilterService, 'list', this.metadata, req, m, onData, () => {}, { waitForEnd: true });
  }

  async getMarketingCampaign(campaign: MarketingCampaign, m: MarketingCampaignMapping) {
    const req = create(pbmcm.MarketingCampaignIdRequestSchema, { id: BigInt(campaign.id) });
    return grpcUnaryOp(MarketingCampaign, this.marketingCampaignManagerService, 'getCampaign', this.metadata, req, m);
  }

  async sendMarketingCampaign(campaign: MarketingCampaign, omitCampaignCheck: boolean, m: MarketingCampaignMapping) {
    const req = create(pbmcm.MarketingCampaignSendRequestSchema, {
      id: BigInt(campaign.id),
      omitCampaignCheck,
    } as any);
    return grpcUnaryOp(MarketingCampaign, this.marketingCampaignManagerService, 'sendCampaign', this.metadata, req, m);
  }

  async archiveMarketingCampaign(campaign: MarketingCampaign, m: MarketingCampaignMapping) {
    const req = create(pbmcm.MarketingCampaignArchiveRequestSchema, {
      id: BigInt(campaign.id),
      isAchived: campaign.isArchived ?? false,
    } as any);
    return grpcUnaryOp(MarketingCampaign, this.marketingCampaignManagerService, 'archiveCampaign', this.metadata, req, m);
  }

  async deleteMarketingCampaign(campaign: MarketingCampaign, m: MarketingCampaignMapping) {
    const req = create(pbmcm.MarketingCampaignIdRequestSchema, { id: BigInt(campaign.id) });
    return grpcUnaryOp(MarketingCampaign, this.marketingCampaignManagerService, 'deleteCampaign', this.metadata, req, m);
  }

  async previewContactFilterForMarketingCampaign(contactFilter: ContactFilter) {
    const req = create(pbmcm.MarketingCampaignPreviewContactFilterRequestSchema, { contactFilterData: contactFilter.data ?? '' });
    const result = await grpcUnaryOpEx((resp: pbmcm.MarketingCampaignPreviewContactFilterResponse) => resp.audienceStats ?? undefined, this.marketingCampaignManagerService, 'previewContactFilter', this.metadata, req, undefined) as {all: bigint, matching: bigint};
    return { all: Number(result.all), matching: Number(result.matching) };
  }

  async testMarketingCampaign(campaign: MarketingCampaign, recipients: string[]) {
    const req = create(pbmcm.MarketingCampaignTestRequestSchema, { id: BigInt(campaign.id), recipients });
    const res = grpcUnaryOpEx(null, this.marketingCampaignManagerService, 'testCampaign', this.metadata, req, undefined) as Promise<void>;
    return res;
  }

  async getMarketingCampaignSettings(m = undefined) {
    const req = create(EmptyRequestSchema, {});
    return grpcUnaryOp(MarketingCampaignSettings, this.marketingCampaignManagerService, 'getSettings', this.metadata, req, m);
  }

  // HtmlTemplate
  async createHtmlTemplate(et: HtmlTemplate, m = undefined) {
    const req = create(pbht.HtmlTemplateRequestSchema, { htmlTemplate: et.toGrpcModel() });
    return grpcUnaryOp(HtmlTemplate, this.htmlTemplateService, 'create', this.metadata, req, m);
  }

  async getHtmlTemplate(et: HtmlTemplate, m = undefined) {
    const req = create(pbht.HtmlTemplateIdRequestSchema, { id: BigInt(et.id) });
    return grpcUnaryOp(HtmlTemplate, this.htmlTemplateService, 'get', this.metadata, req, m);
  }

  async updateHtmlTemplate(et: HtmlTemplate, m = undefined) {
    const req = create(pbht.HtmlTemplateRequestSchema, { htmlTemplate: et.toGrpcModel() });
    return grpcUnaryOp(HtmlTemplate, this.htmlTemplateService, 'update', this.metadata, req, m);
  }

  async deleteHtmlTemplate(et: HtmlTemplate, m = undefined) {
    const req = create(pbht.HtmlTemplateIdRequestSchema, { id: BigInt(et.id) });
    return grpcUnaryOp(HtmlTemplate, this.htmlTemplateService, 'delete', this.metadata, req, m);
  }

  async listHtmlTemplates(onData: OnMultipleDataFnc<HtmlTemplate>, onError: OnErrorFnc, m = undefined) {
    const req = create(pbht.HtmlTemplateListRequestSchema, {});
    return grpcStreamOp(HtmlTemplate, this.htmlTemplateService, 'list', this.metadata, req, m, onData, onError);
  }

  async listAllHtmlTemplates(onData: OnMultipleDataFnc<HtmlTemplate>, m = undefined) {
    const req = create(pbht.HtmlTemplateListRequestSchema, {});
    return grpcStreamOp(HtmlTemplate, this.htmlTemplateService, 'list', this.metadata, req, m, onData, () => {}, { waitForEnd: true });
  }

  // message Center
  async getMessageCenter(m: MessageCenterMapping): Promise<MessageCenter> {
    const req = create(EmptyRequestSchema, {});
    const messageCenter = await grpcUnaryOp(MessageCenter, this.messageCenterService, 'getMessageCenter', this.metadata, req, m);
    return messageCenter;
  }

  async listMessageRules(m: MessageRuleMapping): Promise<MessageRule[]> {
    const req = create(EmptyRequestSchema, {});
    return grpcStreamOpAll(MessageRule, this.messageCenterService, 'listMessageRules', this.metadata, req, m);
  }

  async updateMessageCenterPreferences(data: { settings: MessageSettings, translations: MessageTranslation[] }, m = undefined): Promise<MessageCenterPreferences> {
    let preferences = new MessageCenterPreferences();
    preferences.settings = data.settings;
    preferences.translations = data.translations;

    const req = MessageCenterPreferences.toGrpcRequest(preferences);
    preferences = await grpcUnaryOp(MessageCenterPreferences, this.messageCenterService, 'updateMessagePreferences', this.metadata, req, m);
    return preferences;
  }

  async updateMessageCenterLanguagePreferences(data: { settings: MessageSettings }, m = undefined): Promise<MessageCenterPreferences> {
    let preferences = new MessageCenterPreferences();
    preferences.settings = data.settings;

    const req = MessageCenterPreferences.toGrpcRequest(preferences);
    preferences = await grpcUnaryOp(MessageCenterPreferences, this.messageCenterService, 'updateMessageLanguagePreferences', this.metadata, req, m);
    return preferences;
  }

  async getMessageRule(id: number, m: MessageRuleMapping): Promise<MessageRule> {
    const rule = new MessageRule();
    rule.id = id;

    const req = create(pbmc.MessageRuleRequestSchema, { rule: rule.toGrpcModel() });
    return grpcUnaryOp(MessageRule, this.messageCenterService, 'getMessageRule', this.metadata, req, m);
  }

  async saveMessageRule(rule: MessageRule, m: MessageRuleMapping): Promise<MessageRule> {
    const req = create(pbmc.MessageRuleRequestSchema, { rule: rule.toGrpcModel() });
    return grpcUnaryOp(MessageRule, this.messageCenterService, 'saveMessageRule', this.metadata, req, m);
  }
}
export default new GrpcClient();
